import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { useAvailableApps } from '@/hooks/useAvailableApps';
import AppCard from '@/components/AppCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, RefreshCw } from 'lucide-react';
import { App } from '@/types/App';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import PullToRefreshIndicator from '@/components/PullToRefreshIndicator';

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { selectedDevice } = useDevice();
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const { data: availableAppsData, isLoading, error, retry } = useAvailableApps();

  // Pull to refresh
  const { isPulling, isRefreshing, pullProgress, setContainerRef } = usePullToRefresh({
    onRefresh: async () => {
      retry();
    }
  });

  // Set ref for pull to refresh
  const setRef = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setContainerRef(el);
  }, [setContainerRef]);

  // Flatten and deduplicate apps
  const allAvailableApps = useMemo(() => {
    if (!availableAppsData?.pages) return [];
    return availableAppsData.pages.flatMap(page => page.apps);
  }, [availableAppsData]);

  // Filter apps based on search query
  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return allAvailableApps;
    const query = searchQuery.toLowerCase();
    return allAvailableApps.filter((app: App) => 
      app.name.toLowerCase().includes(query) ||
      app.category?.toLowerCase().includes(query)
    );
  }, [allAvailableApps, searchQuery]);

  const handleAppSelect = (app: App) => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/app/${app.appStoreId}`);
    }
  };

  const handleNavigateToUpdates = () => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/updates`);
    } else {
      navigate('/updates');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <span className="text-destructive text-xl">!</span>
          </div>
          <p className="text-foreground font-medium">Error loading apps</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg border-b border-border">
        <div className="px-5 pt-6 pb-4">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-[28px] font-bold tracking-tight">App Store</h1>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-primary font-semibold hover:bg-primary/10 active:bg-primary/20 active:scale-[0.98] -mr-2 gap-1.5 transition-transform"
              onClick={handleNavigateToUpdates}
            >
              <RefreshCw className="w-4 h-4" />
              Updates
            </Button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
            <Input
              type="search"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-primary/30"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main 
        ref={setRef}
        className="relative overflow-auto"
        style={{ 
          height: 'calc(100vh - 180px)',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <PullToRefreshIndicator 
          isPulling={isPulling} 
          isRefreshing={isRefreshing} 
          pullProgress={pullProgress} 
        />
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading apps...</p>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Search className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-foreground font-medium">
              {searchQuery ? 'No apps found' : 'No apps available'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery ? `Try a different search term` : 'Check back later for new apps'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredApps.map((app: App) => (
              <AppCard 
                key={app.id} 
                app={app} 
                onAppSelect={handleAppSelect}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Index;
